import moment from "moment";
import Axios from "./Axios";
import APIVariables from "./api-variables";

function formatDisplayDate(iso) {
  if (!iso) return "—";
  const m = moment(iso);
  return m.isValid() ? m.format("DD-MMM-YYYY").toUpperCase() : "—";
}

function getUserId(apiUser) {
  return (
    apiUser?.id ??
    apiUser?.user_id ??
    apiUser?.registered_products?.[0]?.user ??
    null
  );
}

/** List/detail may return a user object or a one-element array. */
function unwrapUserManagementResponse(data) {
  if (Array.isArray(data)) return data[0] ?? null;
  if (Array.isArray(data?.results)) return data.results[0] ?? null;
  return data ?? null;
}

function formatProductsSummary(products) {
  if (!Array.isArray(products) || products.length === 0) return "—";
  const names = products.map((p) => p.name).filter(Boolean);
  if (names.length <= 2) return names.join(", ");
  return `${names[0]}, ${names[1]} +${names.length - 2}`;
}

export function mapProductFromApi(apiProduct) {
  if (!apiProduct) return null;
  const allowed = apiProduct.allowed_medication !== false;
  return {
    id: apiProduct.id,
    name: apiProduct.product_display ?? "—",
    expiry_date: apiProduct.expiry_date
      ? formatDisplayDate(apiProduct.expiry_date)
      : "—",
    expiry_date_iso: apiProduct.expiry_date ?? null,
    active: allowed,
    registered: Boolean(apiProduct.registered),
    product_id: apiProduct.product,
    user_id: apiProduct.user,
    raw: apiProduct,
  };
}

function formatChiefPharmacistData(value) {
  if (value === true) return "Yes";
  if (value === false) return "No";
  if (value && typeof value === "object") {
    const name = [value.first_name, value.last_name].filter(Boolean).join(" ");
    return name || value.name || "—";
  }
  return "—";
}

function formatPharmacyLabel(pharmacyData) {
  if (!pharmacyData || typeof pharmacyData !== "object") return "—";
  const name = pharmacyData.value ?? pharmacyData.pharmacy_name;
  const parts = [
    name,
    pharmacyData.postal_code,
    pharmacyData.institution_type,
    pharmacyData.address,
  ].filter(Boolean);
  return parts.length ? parts.join(" — ") : "—";
}

export function mapUserFromApi(apiUser) {
  if (!apiUser) return null;

  const products = (apiUser.registered_products ?? [])
    .map(mapProductFromApi)
    .filter(Boolean);

  const hospitalId = apiUser.hospital_data?.id ?? apiUser.hospital ?? null;
  const hospitalName = apiUser.hospital_data?.name;
  const hospitalAddress = apiUser.hospital_data?.address?.full_address;
  const hospitalLabel =
    [hospitalName, hospitalAddress].filter(Boolean).join(" — ") || "—";

  return {
    id: getUserId(apiUser),
    registration_number:
      apiUser.registration_number_data ?? apiUser.registration_number ?? "—",
    registration_valid:
      products.length > 0 && products.every((p) => p.registered),
    first_name: apiUser.first_name ?? "—",
    last_name: apiUser.last_name ?? "—",
    user_type: apiUser.user_type_display ?? apiUser.user_type ?? "—",
    chief_pharmacist: formatChiefPharmacistData(apiUser.chief_pharmacist_data),
    pharmacy_type: apiUser.pharmacist_role ?? "—",
    pharmacy: formatPharmacyLabel(apiUser.pharmacy_data),
    pharmacy_id: apiUser.pharmacy_data?.id ?? null,
    registered_products: formatProductsSummary(products),
    date_joined: formatDisplayDate(apiUser.date_joined),
    date_joined_iso: apiUser.date_joined ?? null,
    last_logged_in: formatDisplayDate(apiUser.last_login),
    last_login_iso: apiUser.last_login ?? null,
    brochure_downloaded: formatDisplayDate(apiUser.brochure_downloaded),
    brochure_downloaded_iso: apiUser.brochure_downloaded ?? null,
    active_account: Boolean(apiUser.is_active),
    email: apiUser.email ?? "—",
    phone_number: apiUser.phone_number ?? "—",
    hospital: hospitalLabel,
    hospital_id: hospitalId,
    products,
    raw: apiUser,
  };
}

function pickApiString(user, rawKey, mappedKey) {
  const raw = user?.raw;
  const fromRaw = raw?.[rawKey];
  if (fromRaw != null && fromRaw !== "") return fromRaw;
  const mapped = user?.[mappedKey ?? rawKey];
  if (mapped != null && mapped !== "" && mapped !== "—") return mapped;
  return "";
}

/** Form state for the edit user modal. */
export function userToEditFormState(user) {
  if (!user) return null;
  const raw = user.raw ?? {};
  return {
    first_name: pickApiString(user, "first_name"),
    last_name: pickApiString(user, "last_name"),
    email: pickApiString(user, "email"),
    phone_number: raw.phone_number ?? (user.phone_number === "—" ? "" : user.phone_number ?? ""),
    last_login: raw.last_login ?? user.last_login_iso ?? null,
    is_active: raw.is_active ?? user.active_account ?? false,
    hospital_id: user.hospital_id ?? raw.hospital_data?.id ?? raw.hospital ?? null,
    user_type: user.user_type ?? raw.user_type_display ?? "—",
  };
}

/** Payload for PATCH /users/user-management-panel/:id/ */
export function buildUserPatchPayload(user, overrides = {}) {
  const raw = user?.raw ?? {};
  return {
    first_name: pickApiString(user, "first_name"),
    last_name: pickApiString(user, "last_name"),
    is_active: raw.is_active ?? user.active_account ?? false,
    last_login: raw.last_login ?? user.last_login_iso ?? null,
    email: pickApiString(user, "email"),
    phone_number: raw.phone_number ?? (user.phone_number === "—" ? null : user.phone_number ?? null),
    hospital: user.hospital_id ?? raw.hospital_data?.id ?? raw.hospital ?? null,
    ...overrides,
  };
}

const SAVE_ERROR_FIELD_LABELS = {
  hospital: "Hospital",
  first_name: "First name",
  last_name: "Last name",
  email: "Email",
  phone_number: "Phone number",
  is_active: "Active user",
  last_login: "Last login",
  registration_number: "Registration number",
  date_joined: "Date joined",
};

function formatSaveErrorFieldLabel(key) {
  if (SAVE_ERROR_FIELD_LABELS[key]) return SAVE_ERROR_FIELD_LABELS[key];
  return key
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function collectSaveErrorMessages(value, fieldKey = null) {
  if (value == null) return [];

  if (typeof value === "string" && value.trim()) {
    const msg = value.trim();
    return fieldKey ? [`${formatSaveErrorFieldLabel(fieldKey)}: ${msg}`] : [msg];
  }

  if (Array.isArray(value)) {
    return value.flatMap((item) => collectSaveErrorMessages(item, fieldKey));
  }

  if (typeof value === "object") {
    if (fieldKey) {
      return Object.entries(value).flatMap(([nestedKey, nestedValue]) =>
        collectSaveErrorMessages(nestedValue, nestedKey)
      );
    }
    return Object.entries(value).flatMap(([key, nestedValue]) => {
      if (key === "detail") {
        return collectSaveErrorMessages(nestedValue);
      }
      return collectSaveErrorMessages(nestedValue, key);
    });
  }

  const msg = String(value);
  return fieldKey ? [`${formatSaveErrorFieldLabel(fieldKey)}: ${msg}`] : [msg];
}

/** Error messages from PATCH /users/user-management-panel/:id/ (array or object). */
export function parseUserManagementSaveErrors(error) {
  const data = error?.response?.data;
  const messages = collectSaveErrorMessages(data).filter(Boolean);

  if (messages.length > 0) {
    return messages;
  }

  return ["Unable to save user. Please try again."];
}

/** PATCH body from edit form values. */
export function buildUserPatchPayloadFromForm(form) {
  const phone = form.phone_number?.trim();
  return {
    first_name: form.first_name?.trim(),
    last_name: form.last_name?.trim(),
    is_active: Boolean(form.is_active),
    last_login: form.last_login || null,
    email: form.email?.trim(),
    phone_number: phone || null,
    hospital: form.hospital_id,
  };
}

export async function fetchUserManagementList() {
  const res = await Axios.get(APIVariables.USER_MANAGEMENT_PANEL);
  const list = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
  return list.map(mapUserFromApi).filter((u) => u?.id != null);
}

export async function fetchUserManagementDetail(userId) {
  const res = await Axios.get(
    `${APIVariables.USER_MANAGEMENT_PANEL}${userId}/`
  );
  return mapUserFromApi(unwrapUserManagementResponse(res.data));
}

export async function patchUserManagement(userId, payload) {
  const res = await Axios.patch(
    `${APIVariables.USER_MANAGEMENT_PANEL}${userId}/`,
    payload
  );
  return mapUserFromApi(unwrapUserManagementResponse(res.data));
}

export async function setUserManagementActive(user, isActive) {
  return patchUserManagement(
    user.id,
    buildUserPatchPayload(user, { is_active: isActive })
  );
}

/** GET toggles medication activation; returns updated medication record. */
export async function toggleUserMedicationActivation(userMedicationId) {
  const res = await Axios.get(
    `${APIVariables.USER_MEDICATION_ACTIVATION}${userMedicationId}/`
  );
  return mapProductFromApi(res.data);
}

export async function fetchHospitalsList(search = "") {
  const params = search ? `?hospital_name=${encodeURIComponent(search)}` : "";
  const res = await Axios.get(`${APIVariables.HCP_HOSPITALS}${params}`);
  const results = res.data?.results ?? [];
  return results.map((h) => ({
    id: h.id,
    name: h.hospital_name,
    address: h.address,
    postal_code: h.postal_code,
  }));
}
